import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, CheckCircle, Search, Download } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { ResumeScoreForm } from '../../components/admin/resume/ResumeScoreForm';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast'; 

interface ResumeReview {
  id: string;
  resume_url: string;
  status: 'pending' | 'reviewed';
  admin_feedback?: string;
  email: string;
  created_at: string;
  user_profiles: {
    first_name: string;
    last_name: string;
  };
}

interface Comment {
  id: string;
  content: string;
  created_at: string;
  user_profiles: {
    first_name: string;
    last_name: string;
    is_admin: boolean;
  };
}

export function ResumeReviewManagement() {
  const { isAdmin } = useAuth();
  const [reviews, setReviews] = useState<ResumeReview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'reviewed'>('all');
  const [isDownloading, setIsDownloading] = useState<string | null>(null);
  const [selectedReview, setSelectedReview] = useState<ResumeReview | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [showScoreForm, setShowScoreForm] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const { user } = useAuth();
  
  const loadReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('resume_reviews')
        .select(`
          *,
          user_profiles!user_id (
            first_name,
            last_name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('Error loading reviews:', error);
      toast.error('Failed to load resume reviews');
    } finally {
      setIsLoading(false);
    }
  };

  const loadComments = async (reviewId: string) => {
    setIsLoadingComments(true);
    try {
      const { data, error } = await supabase
        .from('resume_review_comments')
        .select(`
          *,
          user_profiles!user_id (
            first_name,
            last_name,
            is_admin
          )
        `)
        .eq('review_id', reviewId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setComments(data || []);
    } catch (error) {
      console.error('Error loading comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setIsLoadingComments(false);
    }
  };

  useEffect(() => {
    loadReviews();
  }, []);

  useEffect(() => {
    if (selectedReview) {
      loadComments(selectedReview.id);
    } else {
      setComments([]);
    }
  }, [selectedReview]);

  // Redirect non-admin users
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const filteredReviews = reviews.filter(review => {
    const searchTerm = searchQuery.toLowerCase();
    const fullName = `${review.user_profiles.first_name} ${review.user_profiles.last_name}`.toLowerCase();
    const matchesSearch = fullName.includes(searchTerm);
    const matchesFilter = filter === 'all' || review.status === filter;
    return matchesSearch && matchesFilter;
  });

  const handleDownload = async (review: ResumeReview) => {
    try {
      setIsDownloading(review.id);
      const response = await fetch(review.resume_url);
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resume-${review.user_profiles.first_name}-${review.user_profiles.last_name}.pdf`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Resume downloaded successfully');
    } catch (error) {
      console.error('Error downloading resume:', error);
      toast.error('Failed to download resume');
    } finally {
      setIsDownloading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#ddd9d9] py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-6">
          <Link
            to="/admin"
            className="inline-flex items-center text-[#800020] hover:opacity-80"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Admin Dashboard
          </Link>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-black">Resume Reviews</h1>
          <div className="mt-4 flex items-center space-x-4">
            <div className="relative flex-1 max-w-lg">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-accent-green focus:border-accent-green"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as typeof filter)}
              className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-black focus:ring-2 focus:ring-accent-green focus:border-accent-green"
            >
              <option value="all">All Reviews</option>
              <option value="pending">Pending</option>
              <option value="reviewed">Reviewed</option>
            </select>
            <div className="text-sm text-gray-500">
              {filteredReviews.length} {filteredReviews.length === 1 ? 'review' : 'reviews'} found
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Submitted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Resume
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredReviews.map((review) => (
                    <tr key={review.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-black">
                          <button
                            onClick={() => setSelectedReview(review)}
                            className="hover:text-[#800020] transition-colors"
                          >
                          {review.user_profiles.first_name} {review.user_profiles.last_name}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {review.status === 'pending' ? (
                            <>
                              <Clock className="h-4 w-4 text-yellow-500 mr-2" />
                              <span className="text-sm text-yellow-500">Pending</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              <span className="text-sm text-green-500">Reviewed</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {new Date(review.created_at).toLocaleDateString()}
                        </div>
                        <button
                          onClick={() => {
                            setSelectedReview(review);
                            setShowScoreForm(true);
                          }}
                          className="ml-4 px-4 py-2 bg-[#2a1114] text-white rounded-lg hover:opacity-90 transition-colors"
                        >
                          Score Resume
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleDownload(review);
                          }}
                          className={`inline-flex items-center text-[#800020] hover:opacity-80 text-sm ${
                            isDownloading === review.id ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <Download className={`h-4 w-4 mr-1 ${
                            isDownloading === review.id ? 'animate-spin' : ''
                          }`} />
                          {isDownloading === review.id ? 'Downloading...' : 'Download Resume'}
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* User Details Modal */}
        {selectedReview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-black">
                    User Details
                  </h2>
                  <button 
                    onClick={() => setSelectedReview(null)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    ×
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Name</h3>
                    <p className="mt-1 text-black">
                      {selectedReview.user_profiles.first_name} {selectedReview.user_profiles.last_name}
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Email</h3>
                    <p className="mt-1 text-black">{selectedReview.email}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Submitted</h3>
                    <p className="mt-1 text-black">
                      {new Date(selectedReview.created_at).toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Comments</h3>
                    {isLoadingComments ? (
                      <div className="flex justify-center py-4">
                        <LoadingSpinner size="sm" />
                      </div>
                    ) : comments.length > 0 ? (
                      <div className="space-y-4">
                        {comments.map((comment) => (
                          <div key={comment.id} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center">
                                <span className="font-medium text-black">
                                  {comment.user_profiles.first_name} {comment.user_profiles.last_name}
                                </span>
                                {comment.user_profiles.is_admin && (
                                  <span className="ml-2 px-2 py-0.5 bg-[#800020] text-white text-xs rounded-full">
                                    Admin
                                  </span>
                                )}
                              </div>
                              <span className="text-sm text-gray-500">
                                {new Date(comment.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-black">{comment.content}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500">No comments yet</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Score Form Modal */}
        {showScoreForm && selectedReview && (
          <ResumeScoreForm
            reviewId={selectedReview.id}
            userId={selectedReview.user_id}
            onClose={() => setShowScoreForm(false)}
            onSuccess={() => {
              setShowScoreForm(false);
              loadReviews();
            }}
          />
        )}
      </div>
    </div>
  );
}